// apps/client/src/world/decor.ts — client-only ground cover (DESIGN.md "Ground cover"): grass tufts, flower clusters of five and cream
// stepping stones, seeded per 16 × 16 chunk from ('decor', townSeed, cx, cy). Three InstancedMeshes for the whole map (contract change 13:
// the world shader clips instances beyond the horizon, so there is no per-chunk culling).
import { tuning } from '@whiskerton/content';
import { groundAt, inRect, rngFrom, type MapData, type Rect } from '@whiskerton/shared';
import { heightAt as tierHeight } from '@whiskerton/shared/map/nav';   // subpath-only (not in the barrel)
import * as THREE from 'three';
import type { GroundWorn } from './terrain/ground';

const TUFT = '#72B862', STEM = '#4A9152', STONE = '#F2E6C4';
/** The stepping stones' and the plaza's cream (the ground's worn earth must never be lighter: tests). */
export const STONE_COLOR = STONE;
/** A stepping stone's radius at scale 1 (the CylinderGeometry's base below). */
const STONE_R = 0.4;
const BLOOMS = ['#FFFFFF', '#F6C85F', '#F4A6C0', '#C9B8F0'] as const;
const STEM_TOP = 0.34, BLOOM_Y = 0.31;   // a stem (a slim tuft) reaches STEM_TOP; the bloom geometry sits at BLOOM_Y so it sways with it

/** Four slim blades leaning out from the centre. */
function tuftGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + 0.4, ca = Math.cos(a), sa = Math.sin(a), w = 0.035, h = 0.26 + (i % 2) * 0.05;
    const bx = ca * 0.03, bz = sa * 0.03, tx = ca * 0.1, tz = sa * 0.1;
    pos.push(bx - sa * w, 0, bz + ca * w, bx + sa * w, 0, bz - ca * w, tx, h, tz);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
  geo.computeVertexNormals();
  return geo;
}

/** The ground cover's group; `userData.flowerSpots` (fx petals) and `userData.worn` (GroundWorn: where each stepping stone and the
 *  plaza's slab lie, so the ground map wears the earth round the shapes actually drawn, light and look round 2). */
export function buildDecor(map: MapData, townSeed: number): THREE.Group {
  const C = tuning.map.chunk;
  const skip: Rect[] = [map.sites.shop, map.sites.museum, ...map.plots.map((p) => p.house)];
  const open = (x: number, z: number) => {
    const tx = Math.floor(x), tz = Math.floor(z);
    return groundAt(map, tx, tz) === 'grass' && !skip.some((r) => inRect(r, tx, tz));
  };
  const ground = (x: number, z: number) => tierHeight(map, { x, y: z });
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), s = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
  const tufts: { m: THREE.Matrix4; c: THREE.Color }[] = [], blooms: typeof tufts = [], stones: THREE.Matrix4[] = [];
  const flowerSpots: THREE.Vector3[] = [], wornStones: { x: number; z: number; r: number }[] = [];
  const at = (x: number, y: number, z: number, yaw: number, sx: number, sy: number, sz: number) =>
    m.compose(p.set(x, y, z), q.setFromAxisAngle(up, yaw), s.set(sx, sy, sz)).clone();
  const plaza = map.sites.plaza, pcx = plaza.x + plaza.w / 2, pcz = plaza.y + plaza.h / 2, prx = plaza.w / 2 + 0.35, prz = plaza.h / 2 + 0.35;
  const onPlaza = (x: number, z: number) => ((x - pcx) / prx) ** 2 + ((z - pcz) / prz) ** 2 < 0.9;

  for (let cy = 0; cy * C < map.h; cy++) for (let cx = 0; cx * C < map.w; cx++) {
    const rng = rngFrom('decor', townSeed, cx, cy), x0 = cx * C, z0 = cy * C;
    const tuftN = rng.int(30, 60);
    for (let i = 0; i < tuftN; i++) {
      const x = x0 + rng.next() * C, z = z0 + rng.next() * C, sc = 0.8 + rng.next() * 0.5, yaw = rng.next() * Math.PI * 2, tone = rng.next();
      if (!open(x, z)) continue;
      tufts.push({ m: at(x, ground(x, z), z, yaw, sc, sc, sc), c: new THREE.Color(TUFT).offsetHSL(0, 0, (tone - 0.5) * 0.08) });
    }
    const clusters = rng.int(2, 6);
    for (let k = 0; k < clusters; k++) {
      const fx = x0 + 1 + rng.next() * (C - 2), fz = z0 + 1 + rng.next() * (C - 2), colour = new THREE.Color(rng.pick(BLOOMS));
      let placed = 0;
      for (let b = 0; b < 5; b++) {
        const a = rng.next() * Math.PI * 2, r = 0.12 + rng.next() * 0.45, x = fx + Math.cos(a) * r, z = fz + Math.sin(a) * r, sc = 0.85 + rng.next() * 0.3;
        if (!open(x, z)) continue;
        const y = ground(x, z);
        tufts.push({ m: at(x, y, z, a, 0.35 * sc, 1.25 * sc, 0.35 * sc), c: new THREE.Color(STEM) });
        blooms.push({ m: at(x, y + (STEM_TOP - BLOOM_Y) * sc, z, a, sc, sc, sc), c: colour });
        placed++;
      }
      if (placed) flowerSpots.push(new THREE.Vector3(fx, ground(fx, fz), fz));
    }
    for (let z = z0; z < Math.min(map.h, z0 + C); z++) for (let x = x0; x < Math.min(map.w, x0 + C); x++) {
      const jx = (rng.next() - 0.5) * 0.2, jz = (rng.next() - 0.5) * 0.2, yaw = rng.next() * Math.PI * 2, sc = 0.85 + rng.next() * 0.3, sq = 0.82 + rng.next() * 0.2;
      if (groundAt(map, x, z) !== 'path' || onPlaza(x + 0.5, z + 0.5)) continue;
      stones.push(at(x + 0.5 + jx, ground(x + 0.5, z + 0.5) + 0.02, z + 0.5 + jz, yaw, sc, 1, sc * sq));
      wornStones.push({ x: x + 0.5 + jx, z: z + 0.5 + jz, r: STONE_R * sc * Math.max(1, sq) });
    }
  }
  // The plaza is one cream disc (DESIGN.md), drawn as a stretched stepping stone in the same batch.
  stones.push(at(pcx, ground(pcx, pcz) + 0.03, pcz, 0, prx / STONE_R, 0.6, prz / STONE_R));
  const worn: GroundWorn = { stones: wornStones, plaza: { x: pcx, z: pcz, rx: prx, rz: prz } };

  const group = new THREE.Group();
  group.name = 'decor';
  const batch = (geo: THREE.BufferGeometry, mat: THREE.Material, list: { m: THREE.Matrix4; c?: THREE.Color }[], name: string) => {
    const mesh = new THREE.InstancedMesh(geo, mat, Math.max(1, list.length));
    list.forEach((e, i) => { mesh.setMatrixAt(i, e.m); if (e.c) mesh.setColorAt(i, e.c); });
    mesh.count = list.length;
    mesh.name = name;
    mesh.receiveShadow = true;
    mesh.computeBoundingSphere();
    group.add(mesh);
    return mesh;
  };
  const leafy = () => new THREE.MeshStandardMaterial({ color: '#FFFFFF', roughness: 0.9, metalness: 0, side: THREE.DoubleSide });
  batch(tuftGeometry(), leafy(), tufts, 'tufts');
  batch(new THREE.IcosahedronGeometry(0.065, 0).scale(1, 0.6, 1).translate(0, BLOOM_Y, 0), leafy(), blooms, 'blooms');
  // The stones lie on the ground, so they take its shade, the low-quality sun shadows and the lamp pools (light and look), not its patches.
  const stone = new THREE.MeshStandardMaterial({ color: STONE, roughness: 0.9, metalness: 0 });
  stone.userData.ground = 'bare';
  batch(new THREE.CylinderGeometry(0.36, STONE_R, 0.07, 16), stone,
    stones.map((sm) => ({ m: sm })), 'stones');
  group.userData.flowerSpots = flowerSpots;
  group.userData.worn = worn;
  return group;
}
